import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import API from "@/lib/api";
import { Lock, Unlock, RefreshCw, AlertCircle, Clock } from "lucide-react";
import { getErrorMessage } from "@/lib/errorHandler";
import { useOrganizationContext } from "@/contexts/OrganizationContext";

export default function LockedAccounts() {
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const [canUnlock, setCanUnlock] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch locked accounts
  const fetchLockedAccounts = async () => {
    try {
      const params = {};

      // For superadmins: filter by organization when one is selected
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }

      const res = await API.get("/admin/locked-accounts", { params });
      setLockedAccounts(res.data.locked_accounts || []);
    } catch (err) {
      console.error("Failed to fetch locked accounts:", err);
      toast.error("Failed to load locked accounts");
    } finally {
      setLoading(false);
    }
  };

  // Check user permissions
  const checkPermissions = async () => {
    try {
      const res = await API.get("/admin/my-permissions");
      setCanUnlock(res.data.can_unlock_accounts || false);
    } catch (err) {
      console.error("Failed to check permissions:", err);
    }
  };

  // Unlock account
  const handleUnlock = async (userId, email) => {
    if (!canUnlock) {
      toast.error("You don't have permission to unlock accounts");
      return;
    }

    if (!window.confirm(`Are you sure you want to unlock ${email}?`)) {
      return;
    }

    setUnlocking(userId);
    try {
      const res = await API.post(`/admin/unlock-account/${userId}`);
      toast.success(res.data.message || "Account unlocked successfully");
      fetchLockedAccounts(); // Refresh list
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to unlock account"));
    } finally {
      setUnlocking(null);
    }
  };

  // Initial load and reload when organization changes
  useEffect(() => {
    fetchLockedAccounts();
    checkPermissions();
  }, [selectedOrg]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLockedAccounts();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Manual refresh
  const handleRefresh = () => {
    setLoading(true);
    fetchLockedAccounts();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Locked Accounts</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage accounts that have been temporarily locked due to failed login attempts
              {isSuperadmin && selectedOrg && (
                <span className="ml-2 text-sm text-blue-600 font-medium">
                  • Filtering by: {selectedOrg.name}
                </span>
              )}
              {isSuperadmin && !selectedOrg && (
                <span className="ml-2 text-sm text-gray-500">
                  • Showing all locked accounts platform-wide
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Permission Warning */}
      {!canUnlock && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">Limited Access</p>
                <p className="text-sm text-amber-700 mt-1">
                  You can view locked accounts, but you don't have permission to unlock them. Contact a super admin if you need unlock access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Currently Locked ({lockedAccounts.length})</CardTitle>
              <CardDescription>
                Accounts will automatically unlock after the lockout period expires
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="auto-refresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="auto-refresh" className="text-sm text-slate-600">
                Auto-refresh
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Loading locked accounts...</span>
            </div>
          ) : lockedAccounts.length === 0 ? (
            <div className="text-center py-12">
              <Lock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No locked accounts</p>
              <p className="text-sm text-slate-400 mt-1">
                All accounts are currently active
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Failed Attempts</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Unlocks In</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Unlock Time</th>
                    {canUnlock && <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {lockedAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900">{account.email}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{account.name}</td>
                      <td className="py-3 px-4">
                        <Badge variant={account.role === 'admin' ? 'default' : 'secondary'}>
                          {account.role}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-red-600">
                          {account.failed_attempts}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700">
                            {account.minutes_remaining} min
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {account.locked_until_formatted}
                      </td>
                      {canUnlock && (
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnlock(account.id, account.email)}
                            disabled={unlocking === account.id}
                            className="text-indigo-600 hover:text-indigo-700 border-indigo-200"
                          >
                            {unlocking === account.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Unlock className="w-4 h-4 mr-1.5" />
                                Unlock
                              </>
                            )}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {lockedAccounts.length > 0 && (
        <div className="mt-4 text-sm text-slate-500 text-center">
          <p>
            Auto-refresh is {autoRefresh ? 'enabled' : 'disabled'}.
            {autoRefresh && ' List updates every 30 seconds.'}
          </p>
        </div>
      )}
    </div>
  );
}
